import * as klavApi from "//fieldandstream.myshopify.com/cdn/shop/t/10/assets/klaviyo-api.mod.js?v=138327477993004780931778618185"
window.klavApi = klavApi

const queryParams = new URLSearchParams(window.location.search);
const IS_DEV = queryParams.has("_dev")
const K_DEV = queryParams.has("_kdev")

if (IS_DEV) {
  document.documentElement.classList.add('_dev')
}

function shopifyForceDefaultFormSubmission(form) {
  const action = form.getAttribute("action")
  if (!action) return false
  const actionPathname = action.startsWith("/")
    ? action
    : new URL(action).pathname
  const shopifySubmittable = actionPathname.startsWith("/contact")
  if (!shopifySubmittable) return false
  const ev = new Event("submit", { bubbles: true, cancelable: true })
  // @ts-expect-error
  ev.__shopifySubmit = true
  form.dispatchEvent(ev)
  return true
}
function formDataToJson(formData) {
  const data = {}
  formData.forEach((value, key) => {
    // Reflect.has in favor of: object.hasOwnProperty(key)
    if (!Reflect.has(data, key)) {
      data[key] = value
      return
    }

    if (!Array.isArray(data[key])) {
      data[key] = [data[key]]
    }
    data[key].push(value)
  })
  return data
}

function formFieldToKeyValue(field, key, value) {
  if (!field || !(field instanceof HTMLElement)) {
    return null
  }
  let name = field.getAttribute("name") || ""
  if (name.startsWith("contact[")) {
    name = name.replace("contact[", "").slice(0, -1)
  }
  key =
    field.getAttribute("gsheet-label") ||
    field.getAttribute("data-gsheet-label") ||
    name ||
    key
  if (!key || key === "form_type" || key === "utf8") {
    return null
  }
  return { key: key, value: value }
}

function mapFormData(form) {
  const defaultFd = new FormData(form)
  const nextFd = new FormData()
  defaultFd.forEach((value, key) => {
    const formField = form[key]
    if (!formField) {
      return
    }
    const mapped = formFieldToKeyValue(formField, key, value)
    if (!mapped || mapped.key == null || mapped.value == null) {
      return
    }
    nextFd.set(mapped.key, mapped.value)
  })
  return nextFd
}

/**
 * @usage
 * User-defined attributes
 * - gsheet-endpoint: Google sheet endpoint
 * - gsheet-label: Label of the form field (should be present on each field)
 * - dev?: Set this to 'true' to enable dev mode - prints the form and does no submit
 * - no-shopify?: Set this to 'true' to prevent Shopify from submitting the form
 * - dehydrated: add this prop - once the element is hydrated, this prop will be removed
 *
 * Component-defined attributes
 * - status: 'idle' | 'pending' | 'idle success' | 'idle error'
 *
 */
export class XMultiForm extends HTMLElement {
  constructor() {
    super();
    this._connected = false
  }

  async connectedCallback() {
    this._connected = true
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (!this._connected) return

    this.setAttribute("status", "idle");
    if (this._ac) this._ac.abort();
    this._ac = new AbortController();

    this._prepareInit();

    this.setAttribute("dev", this._DEV.toString());
    this.init();
    this.removeAttribute("dehydrated");
  }
  async disconnectedCallback() {
    this._connected = false
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (this._connected) return

    if (this._ac) {
      this._ac.abort();
      this._ac = null;
    }
  }

  _prepareInit() {
    this._DEV =
      IS_DEV ||
      this.getAttribute("data-dev") === "true" ||
      this.getAttribute("dev") === "true";

    const dontSubmitToShopify =
      (this.getAttribute("no-shopify") ||
        this.getAttribute("data-no-shopify")) === "true";
    this.submitToShopify = !dontSubmitToShopify;

    const form = this.querySelector("form");
    if (!form) {
      throw new Error(`[x-multi-form] Missing form element`);
    }

    this.form = form;
    this.submitBtns = Array.from(form.querySelectorAll('[type="submit"]'));

    // just trigger a getter for any errors
    // this.sheetsEndpoint;
  }

  init() {
    const _ac = this._ac;
    if (!_ac)
      throw new Error(`[x-multi-form] AbortController not initialized`);

    this.setAttribute("status", "idle");
    this.form.addEventListener(
      "submit",
      (e) => {
        if ("__shopifySubmit" in e) {
          // - `shopifyForceDefaultFormSubmission` triggered an event
          // don't do anything - just let it bubble and shopify will handle it
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const form = this.form;
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }
        this.setAttribute("status", "pending");
        this.disableElements(true);


        return this.subscribeToKlaviyo()
          .then((data) => {
            if (!this._DEV) {
              console.log("Subscribed to klaviyo.", data);

              if (this.submitToShopify) {
                shopifyForceDefaultFormSubmission(this.form);
                return;
              }
            }

            this.setAttribute("status", "idle success");
          })
          .catch((e) => {
            console.error(e);
            this.setAttribute("status", "idle error");
          })
          .finally(() => {
            this.disableElements(false);
          });
      },
      { signal: _ac.signal, capture: true }
    );
  }
  get sheetsEndpoint() {
    if (this._DEV) {
      return "<No endpoint // DEV mode>";
    }

    const endpoint =
      this.getAttribute("gsheet-endpoint") ||
      this.getAttribute("data-gsheet-endpoint");

    if (!endpoint) {
      throw new Error(
        `[x-multi-form] Missing Google sheet endpoint in form ("gsheet-endpoint" attribute)`
      );
    }
    return endpoint;
  }

  async postForm() {
    
  }

  async _postFormToSheets() {
    const fd = mapFormData(this.form);
    if (this._DEV) {
      const formJson = formDataToJson(fd);
      console.log("Form JSON => ", formJson);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ data: formJson });
        }, 500);
      });
    }

    return fetch(this.sheetsEndpoint, {
      method: "POST",
      body: fd,
    })
      .then((r) => r.json())
      .then((data) => {
        return data;
      });
  }
  disableElements(pending) {
    this.submitBtns.forEach((btn) => {
      btn.classList.toggle("btn--loading", !!pending);
      btn.classList.toggle("loading", !!pending);
      const fnName = pending ? "setAttribute" : "removeAttribute";
      btn[fnName]("disabled", "");
    });
    // this.form[fnName]("disabled", "");
  }

  async subscribeToKlaviyo() {
    if (K_DEV) return { ok: true, error: null, data: null }

    const dev = false

    let a = 'Y7Ln5L'
    let g = 'XizN7H'

    let email = this.querySelector('[name="contact\\[email\\]"]')?.value  || ''
    // let name = this.querySelector('[name="name"]')?.value || ''
    // let phone = this.querySelector('[name="phone number"]')?.value  || ''

    if (dev) {
      a = 'QQyd2Z'
      g = 'RzPhNs'
    }

    return klavApi.postNewsletter({
      a,
      g, 
      // name,
      email,
      // $phone_number: phone,
      $source: 'Password Page',
    })
    .then(() => {
      return klavApi.event('Password Page Signup', {
        token: a,
        customer_properties: {
          $email: email,
          "Password Page": true
          // "RSVP for Event": true,
          // "Last RSVP Event": eventJsonOutput.Event,
          // "School": school
        },
        properties: { email }
      })
    })
  }
}

if (!window.customElements.get("x-multi-form")) {
  window.customElements.define("x-multi-form", XMultiForm);
}
